import React, { FC, useState, useEffect, useRef } from 'react';
import { Collapse, Switch, Button, RangePicker, InputNumber } from '@gamesb42/ui-kit';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { TrancheCustomSettings, TrancheCustomInterest } from 'types/TrancheTypes';

import s from './styles.module.scss';

interface TrancheCustomSettingsFormProps {
  customSettings?: TrancheCustomSettings;
  onChange: (settings: TrancheCustomSettings) => void;
}

const TrancheCustomSettingsForm: FC<TrancheCustomSettingsFormProps> = ({ customSettings, onChange }) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(customSettings?.custom_interest_enabled || false);
  const [interests, setInterests] = useState<TrancheCustomInterest[]>(
    customSettings?.interest || [{ period_from: '', period_to: '', interest: 0 }],
  );
  const [isCollapsed, setIsCollapsed] = useState<string[]>(customSettings?.custom_interest_enabled ? ['1'] : []);
  const isInitialized = useRef(false);

  // Синхронизация с входящими данными от сервера (только один раз при загрузке)
  useEffect(() => {
    if (customSettings && Object.keys(customSettings).length > 0 && !isInitialized.current) {
      const enabled = customSettings.custom_interest_enabled || false;
      setIsEnabled(enabled);
      setInterests(customSettings.interest || [{ period_from: '', period_to: '', interest: 0 }]);
      // Если есть кастомные настройки - раскрываем секцию
      setIsCollapsed(enabled ? ['1'] : []);
      isInitialized.current = true;
    }
  }, [customSettings]);

  // Функция для обновления родительского компонента с фильтрацией пустых периодов
  const updateParent = (newInterests = interests) => {
    // Фильтруем только заполненные периоды перед отправкой
    const validInterests = newInterests.filter(
      (period) =>
        period.period_from && period.period_to && period.period_from.trim() !== '' && period.period_to.trim() !== '',
    );

    onChange({
      custom_interest_enabled: isEnabled,
      interest: isEnabled ? validInterests : [],
    });
  };

  const handleSwitchChange = (checked: boolean) => {
    setIsEnabled(checked);
    if (!checked) {
      setInterests([{ period_from: '', period_to: '', interest: 0 }]);
      setIsCollapsed([]);
    } else {
      setIsCollapsed(['1']);
    }
    // Используем текущий список интересов для фильтрации
    setTimeout(() => updateParent(interests), 0);
  };

  const handleInterestChange = (index: number, field: keyof TrancheCustomInterest, value: any) => {
    const newInterests = [...interests];
    newInterests[index] = {
      ...newInterests[index],
      [field]: value,
    };
    setInterests(newInterests);
    setTimeout(() => updateParent(newInterests), 0);
  };

  const handleRangeChange = (index: number, dates: any) => {
    const newInterests = [...interests];
    newInterests[index] = {
      ...newInterests[index],
      period_from: dates && dates[0] ? dates[0].format('YYYY-MM-DD') : '',
      period_to: dates && dates[1] ? dates[1].format('YYYY-MM-DD') : '',
    };
    setInterests(newInterests);
    setTimeout(() => updateParent(newInterests), 0);
  };

  const addPeriod = () => {
    const newInterests = [...interests, { period_from: '', period_to: '', interest: 0 }];
    setInterests(newInterests);
    // Не обновляем родителя - пустой период не попадет в данные благодаря фильтрации
  };

  const removePeriod = (index: number) => {
    if (interests.length > 1) {
      const newInterests = interests.filter((_, i) => i !== index);
      setInterests(newInterests);
      setTimeout(() => updateParent(newInterests), 0);
    }
  };

  return (
    <Collapse
      style={{ marginTop: '32px' }}
      collapsible="header"
      activeKey={isCollapsed}
      onChange={(keys) => setIsCollapsed(Array.isArray(keys) ? keys : [keys].filter(Boolean))}
    >
      <Collapse.Panel header={t('forms.activationOrder.customSettings')} key="1">
        <div className={s.customSettingsForm}>
          <div className={s.switchRow}>
            <Switch checked={isEnabled} size="large" onChange={handleSwitchChange} />
            <span className={s.switchLabel}>{t('forms.activationOrder.changeInterestForPeriod')}</span>
          </div>

          {isEnabled && (
            <div className={s.periodsSection}>
              {interests.map((period, index) => (
                <div key={index} className={s.periodItem}>
                  <div className={s.periodRow}>
                    <RangePicker
                      value={
                        period.period_from && period.period_to
                          ? [moment(period.period_from, 'YYYY-MM-DD'), moment(period.period_to, 'YYYY-MM-DD')]
                          : null
                      }
                      onChange={(dates) => handleRangeChange(index, dates)}
                      style={{ width: '400px' }}
                      label={t('forms.activationOrder.period')}
                      size="large"
                      format="DD.MM.YYYY"
                    />
                    <InputNumber
                      value={period.interest}
                      onChange={(value) => handleInterestChange(index, 'interest', value || 0)}
                      style={{ width: '200px' }}
                      label={t('forms.activationOrder.interestPercent')}
                      size="large"
                      min={0}
                      max={100}
                      precision={2}
                    />
                    {interests.length > 1 && (
                      <Button
                        onClick={() => removePeriod(index)}
                        danger
                        size="large"
                        style={{ height: '60px', alignSelf: 'flex-end' }}
                      >
                        {t('forms.activationOrder.removePeriod')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <Button
                onClick={addPeriod}
                size="large"
                style={{
                  width: '100%',
                  marginTop: '16px',
                  border: '1px dashed #d9d9d9',
                  backgroundColor: 'transparent',
                }}
              >
                + {t('forms.activationOrder.addPeriod')}
              </Button>
            </div>
          )}
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};

export default TrancheCustomSettingsForm;
